import {GameUtils} from "../utils/GameUtils";
import {Config} from "../config/Config";
import {TimelineSprite} from "../../core/animation/TimelineSprite";
import {Game} from "../Game";
import {SymbolContainer} from "../reels/SymbolContainer";
import {gsap} from "gsap";
import {XGodSymbol} from "../reels/XGodSymbol";
import {FireFrameAnimName, Mental2SymbolStates, SYMBOL} from "../enums/Enum";
import {FireFrame} from "../components/FireFrame";
import {SimpleReelModelSymbolData} from "../../core/reel/simplereel/SimpleReelModel";
import {Sound} from "../sound/Sound";
import {ArrayHelper} from "@nolimitcity/slot-game/bin/utils/ArrayHelper";

export class AnticipationView extends PIXI.Container {
    private _fsAnticipationReel4: TimelineSprite;
    private _coolFsAnticipationReel3: TimelineSprite;
    private _coolFsAnticipationReel4: TimelineSprite;
    private _superFsAnticipationReel3: TimelineSprite;
    private _superFsAnticipationReel4: TimelineSprite;
    private _isBgFlickerPlaying: boolean = false;
    private _isReelSetFlickerPlaying: boolean = false;
    private _isSkipped: boolean = false;
    private _s1Count: number = 0;
    private _s2Count: number = 0;
    private _xGodPosArr: number[] = [];
    private _xGodFireReel: FireFrame;
    private _xGodFireFrame: FireFrame[] = [];
    private _scatterSymArr: TimelineSprite[] = [];
    private _isXGodFailSoundPlayed: boolean = false;

    // #356
    public xGodReelSpinPreviousSound: string = "";

    constructor() {
        super();
        this.name = "AnticipationView";
        this.init();
    }

    private static createImageSeq(name: string, animPos: PIXI.Point, scaleY: number = 1): TimelineSprite {
        const imageSeq: TimelineSprite = GameUtils.createTimelineSprite(name, 30, animPos);
        imageSeq.anchor.set(0.5);
        imageSeq.blendMode = PIXI.BLEND_MODES.ADD;
        imageSeq.scale.y = scaleY;
        imageSeq.hide();

        return imageSeq;
    }

    public handleXGodStuffs(reelId: number): void {
        const rowId: number = Game.model.xGodInReels[reelId];
        if (rowId > -1) {
            if (reelId === 0) {
                // #356
                let xGodReelSpinSound: string = ArrayHelper.getRandomValue(Sound.X_GOD_REEL_SPINNING);
                while(this.xGodReelSpinPreviousSound === xGodReelSpinSound) {
                    xGodReelSpinSound = ArrayHelper.getRandomValue(Sound.X_GOD_REEL_SPINNING);
                }
                this.xGodReelSpinPreviousSound = xGodReelSpinSound;
                Game.soundPlayer.playSound(this.xGodReelSpinPreviousSound, 1, Number.MAX_VALUE);
            }
            const xGodSymContainer: SymbolContainer = <SymbolContainer>Game.reelView.reelModels[reelId].getStopSymbolAt(rowId).positionable;
            const xGodSym: XGodSymbol = <XGodSymbol>xGodSymContainer.symbols[0];
            this._xGodPosArr.push(reelId * 3 + rowId);
            if (reelId < 4) {
                GameUtils.shake(0, 10, 60 * reelId, (reelId + 1), 0.2);
            }
            if (reelId === 0) {
                Game.foregroundElements.playReelSetFlicker();
                Game.blackBG.showWithTimeline(0.7, 0.1);
            }
            Game.foregroundElements.featureLogoContainer.playXGodBlink(0.4);
            xGodSym.playAnticipation();
            this.playXGodFireFrame(reelId, rowId);
            //next reel stuffs
            if (reelId < 4) {
                this.playXGodFireReel(reelId + 1);
            }
            if (reelId === 3) {
                Game.soundPlayer.playSound(Sound.X_GOD_FINAL_REEL_ANTICIPATION);
            }
        } else if (Game.model.xGodInReels[0] !== -1) {
            this.offXGodModeStuffs(reelId);
        }
    }

    public handleXGodSounds(reelId: number) {
        if (!this._isXGodFailSoundPlayed && Game.model.xGodInReels[0] > -1) {
            if (Game.model.xGodInReels[reelId] > -1) {
                Game.soundPlayer.playSound(Sound.X_GOD_SYMBOL_LANDING);
            } else {
                Game.soundPlayer.playSound(Sound.X_GOD_FAIL);
                Game.soundPlayer.stopSound(this.xGodReelSpinPreviousSound);
                Game.soundPlayer.fadeSound(0, 1, 400, Game.soundPlayer.currentMusic);
                this._isXGodFailSoundPlayed = true;
            }
        }
    }

    public offXGodModeStuffs(currentReelId: number): void {
        if (currentReelId === -1) {
            this._isXGodFailSoundPlayed = false;
        } else {
            //set miss reel set to reels
            for (let i: number = currentReelId + 1; i < 5; i++) {
                Game.reelView.reelModels[i].setReelSet(Config.XGOD_MISS_REELSET);
            }
            if (Game.model.xGodInReels.indexOf(-1) === currentReelId) {
                for (let reelId: number = 0; reelId <= currentReelId; reelId++) { //disable all symbol
                    Game.reelView.reelModels[reelId].getAllVisibleStopSymbols().forEach((reelsData: SimpleReelModelSymbolData) => {
                        (reelsData.positionable as SymbolContainer).setState(Mental2SymbolStates.NO_WIN);
                    });
                }
            } else {
                Game.reelView.reelModels[currentReelId].getAllVisibleStopSymbols().forEach((reelsData: SimpleReelModelSymbolData) => {
                    (reelsData.positionable as SymbolContainer).setState(Mental2SymbolStates.NO_WIN);
                });
            }
        }

        this._xGodPosArr.forEach((value: number) => {
            const reelId: number = Math.floor(value / 3);
            const rowId: number = value % 3;
            const xGodSymContainer: SymbolContainer = <SymbolContainer>Game.reelView.reelModels[reelId].getStopSymbolAt(rowId).positionable;
            const xGodSym: XGodSymbol = <XGodSymbol>xGodSymContainer.symbols[0];
            xGodSym.stopAnticipation();
        });
        this._xGodFireFrame.forEach((fireFrame: FireFrame) => {
            fireFrame.stop().add(() => {
                fireFrame.parent?.removeChild(fireFrame);
            });
        })
        if (this._xGodFireReel) {
            this._xGodFireReel.stop(); //stop reel fireFrame
        }
        Game.foregroundElements.stopReelSetFlicker();
        if (!Game.model.isXGodMaxWinTriggered()) {
            Game.blackBG.hide(0.2);
        }
        this._xGodFireFrame.length = 0;
        this._xGodPosArr.length = 0;
    }

    public reset() {
        this._s1Count = 0;
        this._s2Count = 0;
        this._isSkipped = false;
    }

    public playScatterAnticipation(reelId: number) {
        Config.IS_SCATTER_ANTICIPATION_REEL_ACTIVE[reelId] = true;
        if (this._s1Count === 2 && this._s2Count === 0 && reelId === 3) {
            this._coolFsAnticipationReel3.show();
            this._coolFsAnticipationReel3.playLoop();
        } else if (this._s1Count == 2 && this._s2Count === 0 && reelId === 4) {
            this._fsAnticipationReel4.show();
            this._fsAnticipationReel4.playLoop();
        } else if (this._s1Count === 2 && this._s2Count >= 1 && reelId === 3) {
            this._superFsAnticipationReel3.show();
            this._superFsAnticipationReel3.playLoop();
        } else if (this._s1Count === 2 && this._s2Count === 1 && reelId === 4) {
            this._coolFsAnticipationReel4.show();
            this._coolFsAnticipationReel4.playLoop();
        } else if (this._s1Count >= 2 && this._s2Count === 2 && reelId === 4) {
            this._superFsAnticipationReel4.show();
            this._superFsAnticipationReel4.playLoop();
        }

        if (this._s1Count >= 2 && !this._isBgFlickerPlaying) {
            this._isBgFlickerPlaying = true;
            Game.backgroundElements.playFlickerAnim(true);
        }

        if (!this._isReelSetFlickerPlaying) {
            if (this._s1Count >= 2 && ((this._s2Count === 1 && reelId === 3) || (this._s2Count === 2 && reelId === 4))) {
                this._isReelSetFlickerPlaying = true;
                Game.backgroundElements.stopFlickerAnim();
                Game.backgroundElements.playFlickerAnim(true);
                Game.foregroundElements.playReelSetFlicker();
            }
        }

    }

    public stopScatterAnticipation(reelId: number) {
        Config.IS_SCATTER_ANTICIPATION_REEL_ACTIVE[reelId] = false;
        this._coolFsAnticipationReel3.stopLoop();
        this._coolFsAnticipationReel3.hide();

        this._coolFsAnticipationReel4.stopLoop();
        this._coolFsAnticipationReel4.hide();

        this._fsAnticipationReel4.stopLoop();
        this._fsAnticipationReel4.hide();

        this._superFsAnticipationReel3.stopLoop();
        this._superFsAnticipationReel3.hide();

        this._superFsAnticipationReel4.stopLoop();
        this._superFsAnticipationReel4.hide();
    }


    public updateScatterCount(reelId: number) {
        if (Game.model.data.evaluatedArea[reelId].includes("S1")) {
            this._s1Count++;
        }
        if (Game.model.data.evaluatedArea[reelId].includes("S2")) {
            this._s2Count++;
        }
    }

    public scatterReelHighlightAnticipation(reelId: number, isSkipped: boolean) {
        this._isSkipped = isSkipped
        if (Config.IS_SCATTER_ANTICIPATION_REEL_ACTIVE[reelId] && reelId >= 1) {
            this.stopScatterAnticipation(reelId);
            if (this._isReelSetFlickerPlaying && this._s2Count < 2) {
                this.stopBgAnticipationEffects();
            }
        }

        if (reelId > 0 && reelId !== 4 && !this._isSkipped && Game.model.scatterAnticipationReels[reelId + 1]) {
            this.playScatterAnticipation(reelId + 1);
        }
    }

    public playScatterSymbolAnticipation(reelId: number): gsap.core.Timeline {
        const symbolTl: gsap.core.Timeline = gsap.timeline();
        if (this._s1Count >= 2) {
            if (reelId === 2) {
                symbolTl.add(() => {
                    Game.soundPlayer.playSound(Sound.S1_ANTICIPATION);
                });
                for (let i: number = 0; i <= 2; i++) {
                    symbolTl.add(this.scatterSymbolAnticipation(i), 0);
                }
            } else if (reelId > 2) {
                symbolTl.add(this.scatterSymbolAnticipation(reelId));
            }
        }
        return symbolTl;
    }

    public stopBgAnticipationEffects() {
        if (this._isBgFlickerPlaying) {
            Game.backgroundElements.stopFlickerAnim();
            this._isBgFlickerPlaying = false;
        }
        if (this._isReelSetFlickerPlaying) {
            Game.foregroundElements.stopReelSetFlicker();
            this._isReelSetFlickerPlaying = false;
        }
    }

    private playXGodFireFrame(reelId: number, rowId: number): void {
        const fireFrame: FireFrame = new FireFrame(reelId, rowId);
        let fireFrameSize: FireFrameAnimName;
        if (reelId === 2 && rowId === 0) {
            fireFrameSize = FireFrameAnimName.HALF;
        } else {
            fireFrameSize = FireFrameAnimName.SYMBOL;
        }
        fireFrame.play(fireFrameSize);
        this.addChild(fireFrame);
        this._xGodFireFrame.push(fireFrame);
    }

    private playXGodFireReel(targetReelId: number): void {
        let size: FireFrameAnimName;
        if (targetReelId === 2) {
            size = FireFrameAnimName.SYMBOL_X2_HALF;
        } else if (targetReelId === 1 || targetReelId == 3) {
            size = FireFrameAnimName.SYMBOL_X2;
        } else {
            size = FireFrameAnimName.SYMBOL_X3;
        }
        const pos: PIXI.Point = GameUtils.getSymbolPos(targetReelId, 1);
        if (!this._xGodFireReel) {
            this._xGodFireReel = new FireFrame(targetReelId, 1);
            this.addChild(this._xGodFireReel);
        }
        this._xGodFireReel.playLoop(size).add(() => {
            this._xGodFireReel.position.set(pos.x + 8, pos.y - 4);
        }, 0);
    }

    private createAndPlayFireFrame(reelId: number, rowId: number, size: FireFrameAnimName): gsap.core.Timeline {
        const fireFrame: FireFrame = new FireFrame(reelId, rowId);
        const tl: gsap.core.Timeline = fireFrame.play(size);
        return tl;
    }

    private init() {

        const reel3Pos: PIXI.Point = new PIXI.Point(504, 415);
        const reel4Pos: PIXI.Point = new PIXI.Point(648, 415);
        this._fsAnticipationReel4 = AnticipationView.createImageSeq("cool_fs_anticipation/cool_fs_anticipation_00", reel4Pos, 1);

        this._coolFsAnticipationReel3 = AnticipationView.createImageSeq("cool_fs_anticipation/cool_fs_anticipation_00", reel3Pos, 0.65);
        this._coolFsAnticipationReel4 = AnticipationView.createImageSeq("cool_fs_anticipation/cool_fs_anticipation_00", reel4Pos, 1);

        this._superFsAnticipationReel3 = AnticipationView.createImageSeq("super_fs_anticipation/super_fs_anticipation_00", reel3Pos, 0.65);
        this._superFsAnticipationReel4 = AnticipationView.createImageSeq("super_fs_anticipation/super_fs_anticipation_00", reel4Pos, 1);

        this.addChild(this._fsAnticipationReel4, this._coolFsAnticipationReel3, this._coolFsAnticipationReel4, this._superFsAnticipationReel3, this._superFsAnticipationReel4);
        this.position.set(0, Config.MARGIN_TOP);
    }

    private scatterSymbolAnticipation(currentReelNo: number): gsap.core.Timeline {
        const nearWinTl: gsap.core.Timeline = gsap.timeline();
        if (Game.model.scatterLandedPos[currentReelNo] === -1) {
            return nearWinTl;
        }
        const scatterSymContainer: SymbolContainer = GameUtils.getSymbol(currentReelNo, Game.model.scatterLandedPos[currentReelNo]);
        if (!this._isSkipped && Game.model.scatterAnticipationReels.includes(true) && currentReelNo !== 4) {
            nearWinTl.add(() => {
                for (let i = 0; i < scatterSymContainer.symbols.length; i++) {
                    let scatterSym = scatterSymContainer.symbols[i];
                    const animName: string = scatterSym.state.tracks[0].animation.name.split("_")[2];
                    scatterSym.state.setAnimation(0, animName + "_anticipation", true);

                    const scatterAnticipationEffect: TimelineSprite = Game.effectsView.createScatterAnticipationEffect(scatterSym.currentHeight);
                    scatterAnticipationEffect.position.set(scatterSymContainer.position.x - 2, scatterSymContainer.parent.position.y + scatterSymContainer.position.y + scatterSym.position.y + 216);
                    this._scatterSymArr.push(scatterAnticipationEffect);
                    scatterAnticipationEffect.playLoop();
                }
            });
        }
        return nearWinTl;
    }

    get scatterSymArr(): TimelineSprite[] {
        return this._scatterSymArr;
    }

    set scatterSymArr(value: TimelineSprite[]) {
        this._scatterSymArr = value;
    }
}